import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, loadStorefront, office } from "@/lib/api";
import { mediaUrl } from "@/lib/media";
import { session, type OfficeUser } from "@/lib/session";
import { Button, Field, Input } from "@/components/ui";
import { useInstall } from "@/lib/install";

/** Sign in with the store admin account (the same one as the website's admin). Till PINs don't work here. */
export function Login() {
  const store = useQuery({ queryKey: ["storefront"], queryFn: loadStorefront, staleTime: 3600_000 });
  const app = useInstall();
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const name = store.data?.storeName ?? "Shop";
  const logo = store.data?.settings?.logo;
  const submit = async () => {
    setBusy(true);
    setErr("");
    try {
      const r = await api<{ token: string; user: OfficeUser }>(office("/login"), { body: { login, password } });
      session.signIn(r.token, r.user);
    } catch (e) {
      setErr(e instanceof TypeError ? "Can't reach the shop server. Check the internet and try again" : e instanceof Error ? e.message : "That didn't work");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="grid min-h-full place-items-center bg-[radial-gradient(120%_90%_at_0%_0%,#14393d_0%,#0a1618_58%)] p-4">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        className="flex w-full max-w-[400px] flex-col gap-4 rounded-[22px] bg-white p-6 shadow-[0_30px_70px_rgba(0,0,0,0.4)] sm:p-7"
      >
        <span className="flex items-center gap-3">
          {logo ? <img src={mediaUrl(logo, 300)} alt="" className="h-9 w-auto max-w-[140px] object-contain" /> : <span className="grid h-10 w-10 place-items-center rounded-xl bg-brand text-sm font-extrabold text-white">{name.trim()[0]?.toUpperCase()}</span>}
          <span className="flex flex-col">
            <b className="text-[15px]">{name}</b>
            <span className="text-[12.5px] text-muted">Shop back office</span>
          </span>
        </span>
        <div>
          <h1 className="text-[22px] font-extrabold tracking-tight">Sign in</h1>
          <p className="mt-1 text-[13px] text-muted">Use the email or phone number and password you sign in to the store admin with. Till PINs don't work here.</p>
        </div>
        <Field label="Email or phone number">
          <Input autoFocus autoComplete="username" value={login} onChange={(e) => setLogin(e.target.value)} />
        </Field>
        <Field label="Password">
          <Input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        {err ? (
          <p role="alert" className="text-[13px] font-semibold text-stop">
            {err}
          </p>
        ) : null}
        <Button type="submit" tone="primary" size="lg" disabled={busy || !login || !password}>
          {busy ? "Signing in…" : "Sign in"}
        </Button>
        {app.canPrompt ? (
          <button type="button" onClick={() => void app.install()} className="text-sm font-semibold text-brand underline">
            Install the back office as an app
          </button>
        ) : app.apple ? (
          <span className="text-center text-xs text-slate-500">To install: tap Share, then Add to Home Screen.</span>
        ) : null}
      </form>
    </div>
  );
}
